import { ref, onMounted, onUnmounted } from 'vue'

const CHECK_INTERVAL = 5 * 60 * 1000 // 5 minutes
const VERSION_URL = '/version.json'

// Shared state across components
const updateAvailable = ref(false)
let currentVersion = null
let intervalId = null
let initialized = false

/**
 * Polls version.json to detect new deployments.
 * Shows an update notification when the build hash changes.
 *
 * Usage: call useVersionCheck() once in your App.vue or layout component.
 * The composable returns { updateAvailable, dismissUpdate, applyUpdate }.
 */
export function useVersionCheck() {
  async function checkVersion() {
    try {
      const res = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: 'no-store',
      })
      if (!res.ok) return

      const data = await res.json()
      if (!currentVersion) {
        // First check — store the current version
        currentVersion = data.version
        return
      }
      if (data.version !== currentVersion) {
        updateAvailable.value = true
      }
    } catch {
      // Network error or version.json doesn't exist yet — ignore
    }
  }

  function applyUpdate() {
    window.location.reload()
  }

  function dismissUpdate() {
    updateAvailable.value = false
  }

  onMounted(() => {
    if (!initialized) {
      initialized = true
      // Initial check after a short delay (don't block app load)
      setTimeout(checkVersion, 3000)
      intervalId = setInterval(checkVersion, CHECK_INTERVAL)
    }
  })

  onUnmounted(() => {
    // Don't clear — the interval should persist across route changes
    // It will be cleaned up when the page unloads
  })

  return {
    updateAvailable,
    applyUpdate,
    dismissUpdate,
  }
}
